import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import ListItemDiv from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemDiv';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import DeviationActions from '../../../../../../tableActions/DeviationActions';
import DeviationThumb from '../../../../../../StudioDeviationList/_partials/DeviationThumb';
import ClientIdField from '../../../../../../StudioDeviationList/items/ApplicationListItem/_partials/ClientIdField';
import ClientSecretField from '../../../../../../StudioDeviationList/items/ApplicationListItem/_partials/ClientSecretField';
import { useMainActionDeviationClick } from '../../../../../../../hooks/useMainActionDeviationClick';
import { getAppDataById } from '../../../../../../../selectors/studio/apps';
import { AppState } from '../../../../../../../types/store';
import { Props } from '../../types';

import s from './AppsLayout.scss';

const AppsLayout: FC<Props> = ({
  deviation,
  offset,
  scrollToRef,
  isSelected,
  isInSelectMode,
  className,
  onChange,
}) => {
  const appData =
    useSelector((state: AppState) => getAppDataById(state, deviation.deviationId)) ?? {};
  const { onClick, mainAction } = useMainActionDeviationClick(deviation, scrollToRef, 'list-item');

  const onItemClick = useCallback(
    (ev: MouseEvent, clickedDeviation: PapiDeviation) => {
      // 1. When in selection mode, clicking the item selects it,
      // otherwise it takes the user to edit or view...
      if (!isInSelectMode) {
        // 2. ...but use preventDefault to prevent selection on the way...
        // 3. ...and also: useDuperbrowseLink won't open duper if ev.defaultPrevented
        //    (so don't do it for 'view')
        mainAction !== 'view' && ev.preventDefault();
        onClick(ev);
      }
    },
    [isInSelectMode, mainAction, onClick]
  );

  return (
    <ListItemDiv
      className={cn(s['root'], className)}
      label={<div className={s['title']}>{deviation.title}</div>}
      prefix={
        <div className={s['prefix']}>
          <Checkbox inputClassName={s['input']} isChecked={isSelected} onChange={onChange} />
          <DeviationThumb deviation={deviation} isAppThumb />
        </div>
      }
      suffix={
        isInSelectMode ? null : (
          <DeviationActions
            className={s['actions']}
            deviation={deviation}
            offset={offset}
            onlyContextMenu
          />
        )
      }
      bottomSlot={
        <div className={s['app-data']}>
          <ClientIdField value={appData.clientId ?? ''} />
          <ClientSecretField value={appData.clientSecret ?? ''} />
        </div>
      }
      onLabelClick={ev => onItemClick(ev, deviation)}
      checked={isSelected}
      isLabelBold
    />
  );
};
AppsLayout.displayName = 'AppsLayout';

export default AppsLayout;
